import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, AuthState, LoginForm } from '../../types';
import { authService } from '../../services/auth/authService';

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginForm, { rejectWithValue }) => {
    try {
      const response: any = await authService.login(credentials);
      console.log('Login response:', response);
      localStorage.setItem('token', response.token);
      
      // Transform backend response to match frontend User interface
      // Backend sends: { _id, email, name, token, type }
      // Frontend expects: { user: { id, email, name, role, ... }, token }
      return {
        user: {
          id: response._id,
          email: response.email,
          name: response.name,
          role: response.type, // Map 'type' to 'role'
          company_features: { advance_tax: true, carton_discount_pricing: false, sales_tax_invoice: false, loose_pcs: false, ...response.company_features },
          company_uom: { code: 'CTN', singular: 'Carton', plural: 'Cartons', allows_decimal: false, ...response.company_uom },
          company_branding: response.company_branding,
          createdAt: response.createdAt || new Date().toISOString(),
          updatedAt: response.updatedAt || new Date().toISOString(),
        },
        token: response.token,
      };
    } catch (error: any) {
      console.log('Login error:', error);
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      localStorage.removeItem('token');
      return true;
    } catch (error: any) {
      // Even if logout fails on server, clear local state
      localStorage.removeItem('token');
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const userData = await authService.getCurrentUser();
      // Transform backend response to match frontend User interface
      return {
        id: userData._id,
        email: userData.email,
        name: userData.name,
        role: userData.type, // Map 'type' to 'role'
        company_features: { advance_tax: true, carton_discount_pricing: false, sales_tax_invoice: false, loose_pcs: false, ...userData.company_features },
        company_uom: { code: 'CTN', singular: 'Carton', plural: 'Cartons', allows_decimal: false, ...userData.company_uom },
        company_branding: userData.company_branding,
        createdAt: userData.createdAt || new Date().toISOString(),
        updatedAt: userData.updatedAt || new Date().toISOString(),
      };
    } catch (error: any) {
      // If fetching user fails (e.g., invalid token), clear auth state
      localStorage.removeItem('token');
      return rejectWithValue(error.message || 'Failed to fetch user');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      localStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        loginUser.fulfilled,
        (
          state,
          action: PayloadAction<{ user: User; token: string }>
        ) => {
          state.loading = false;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
      )
      .addCase(loginUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });
  },
});

export const { clearError, setLoading, clearAuth } = authSlice.actions;
export default authSlice.reducer;
